import React, { forwardRef } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { OrgNotificationModel } from '../../../models/notification/org-notification.model'
import { useService } from '@pushly/aqe/lib/hooks'
import { AppState } from '../../../stores/app'
import { OrgNotificationBuilderViewProps } from './internals'
import { NotificationVariantModel } from '../../../models/notification/notification-variant.model'
import { getVariantDisplayLabel } from '../helpers'
import { VariantManagerProvider } from '@pushly/aqe/lib/components'
import { OrgNotificationProvider } from '../../../providers/org-notification-provider/org-notification-provider'
import { NotificationBuilderRef } from '../notification-builder'
import { OrgNotificationBuilderViewInternals } from './internals/components/org-notification-builder-view-internals'
import { OrgProvider } from '../../../providers/org-provider/org-provider'
import { useActiveOrgChangeEffect } from '../../../hooks/use-active-org-change-effect'
import { useNotificationBuilderViewResourceFetchErrorCallback } from './internals/hooks/use-notification-builder-view-resource-fetch-error-callback'

type NotificationBuilderViewPathParams = {
    orgId?: string
    accountId?: string
    notificationId?: string
}

export const OrgNotificationBuilderView = forwardRef<NotificationBuilderRef, OrgNotificationBuilderViewProps>(
    (props, ref) => {
        const appState = useService(AppState)

        useActiveOrgChangeEffect((nextOrgId, appSvc) => {
            appSvc.route(`organizations/${nextOrgId}/notifications`)
        })

        const {
            orgId: _orgIdPathParam,
            accountId: _accountIdPathParam,
            notificationId: notifIdPathParam,
        } = useParams<NotificationBuilderViewPathParams>()

        const orgIdPathParam = _orgIdPathParam ?? _accountIdPathParam

        const { state: routeState, search: searchParamsStr } = useLocation<{
            notification?: OrgNotificationModel
        }>()

        const handleNotificationsFetchError = useNotificationBuilderViewResourceFetchErrorCallback({
            level: 'org',
            orgId: orgIdPathParam,
            resolveParentRoute: props.getParentRoute,
            onFetchError: props.onNotificationsFetchError,
        })

        const searchParams = new URLSearchParams(searchParamsStr)
        const duplicateSourceIdQueryParam = searchParams.get('notification_id')

        const notificationPathId = notifIdPathParam ?? notifIdPathParam
        const notificationSourceId = notificationPathId ?? duplicateSourceIdQueryParam

        if (!orgIdPathParam) {
            throw new Error('Missing orgId or accountId')
        }
        const orgId = Number(orgIdPathParam)

        if (!appState.currentDomain) {
            // Rendering should be prevented until the appState has fully loaded.
            return null
        }

        return (
            <OrgProvider orgId={orgId}>
                <OrgNotificationProvider
                    orgId={orgId}
                    notificationId={notificationSourceId}
                    duplicateSourceId={duplicateSourceIdQueryParam}
                    notification={routeState?.notification}
                    onFetchError={handleNotificationsFetchError}
                >
                    <VariantManagerProvider<NotificationVariantModel>
                        getVariantIdentity={(v) => v.getId()}
                        getVariantLabel={(_v, variantIdx, totalVariants) =>
                            getVariantDisplayLabel(variantIdx, totalVariants ?? 1)
                        }
                    >
                        <OrgNotificationBuilderViewInternals
                            ref={ref}
                            {...props}
                            levelId={orgId}
                            requestedNotifId={notificationPathId ? Number(notificationPathId) : undefined}
                            // Pushly AI is explicitly disabled at the org level
                            // until org level A/B testing is available.
                            hidePushlyAiToggle={true}
                            hidePageAnalysisToggle={true}
                        />
                    </VariantManagerProvider>
                </OrgNotificationProvider>
            </OrgProvider>
        )
    },
)
